import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ImagePlus, Plus, Search, X } from 'lucide-react';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  MembershipPlan,
  MembershipPlanDoctor,
  createMembershipPlan,
  fetchMembershipPlanDoctors,
  fetchMembershipPlans,
  membershipPromoMediaKey,
  deleteMembershipPlan,
  setMembershipPlanActive,
  updateMembershipPlan,
} from '../../../services/MasterAdmin/membershipService';
import {
  deleteMediaAsset,
  fetchMediaAssets,
  fetchPublicMediaByKey,
  uploadMediaAsset,
} from '../../../services/MasterAdmin/mediaAssetService';
import MembershipPanelShell from './MembershipPanelShell';
import MembershipPlanActionsMenu from './MembershipPlanActionsMenu';
import MembershipPlanViewModal from './MembershipPlanViewModal';
import { MembershipError, MembershipLoading, MembershipSuccess, StatusBadge } from './MembershipAlerts';
import { msInput, msPrimaryBtn, msSecondaryBtn, msTableWrap, msTd, msTh } from './membershipUi';

const PLAN_TYPE_DEFAULTS: Record<string, Partial<MembershipPlan>> = {
  Starter: {
    title: '1 month',
    duration: '1m',
    sortOrder: 1,
    consultationCreditsTotal: 1,
    consultationCreditCarryForward: false,
    maxCarryForwardCredits: 0,
    storageQuotaBytes: 100 * 1024 * 1024,
    maxTransfersPerYear: 0,
    ambulancePriority: false,
  },
  Silver: {
    title: '3 months',
    duration: '3m',
    sortOrder: 2,
    consultationCreditsTotal: 3,
    consultationCreditCarryForward: true,
    maxCarryForwardCredits: -1,
    storageQuotaBytes: 200 * 1024 * 1024,
    maxTransfersPerYear: 1,
    ambulancePriority: false,
  },
  Gold: {
    title: '6 months',
    duration: '6m',
    sortOrder: 3,
    consultationCreditsTotal: 6,
    consultationCreditCarryForward: true,
    maxCarryForwardCredits: -1,
    storageQuotaBytes: 300 * 1024 * 1024,
    maxTransfersPerYear: 2,
    ambulancePriority: true,
  },
  Platinum: {
    title: '12 months',
    duration: '12m',
    sortOrder: 4,
    consultationCreditsTotal: 12,
    consultationCreditCarryForward: true,
    maxCarryForwardCredits: -1,
    storageQuotaBytes: 400 * 1024 * 1024,
    maxTransfersPerYear: 4,
    ambulancePriority: true,
  },
};
const emptyPlanForm: Partial<MembershipPlan> = {
  name: '',
  title: '1 month',
  type: 'Starter',
  price: 199,
  duration: '1m',
  description: '',
  isActive: true,
  isPopular: false,
  maxFamilyMembers: 12,
  consultationCreditsPerMonth: 1,
  consultationCreditsTotal: 1,
  consultationCreditCarryForward: false,
  maxCarryForwardCredits: 0,
  storageQuotaBytes: 104857600,
  maxTransfersPerYear: 0,
  gpConsultFee: 250,
  vedikaCommissionPercent: 20,
  dedicatedAmbulanceNumber: '',
  ambulancePriority: false,
  allowedSpecialities: [],
  allowedDoctors: [],
  promoImageUrl: null,
  promoImageMediaKey: null,
  promoImageOrientation: 'horizontal',
  gstPercent: 0,
  gstMode: 'exclusive',
  sortOrder: 1,
};

type CatalogDoctor = {
  vendorId: string;
  doctorName: string;
  clinicName?: string;
  specialities: Array<{ specialityId: string; name: string }>;
};

const MembershipPlansPanel: React.FC = () => {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [doctorQuery, setDoctorQuery] = useState('');
  const [doctorResults, setDoctorResults] = useState<CatalogDoctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState<Partial<MembershipPlan> | null>(null);
  const [viewingPlan, setViewingPlan] = useState<MembershipPlan | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadingPromo, setUploadingPromo] = useState(false);
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rank: Record<string, number> = { Starter: 1, Silver: 2, Gold: 3, Platinum: 4 };
      setPlans(
        [...(await fetchMembershipPlans())].sort(
          (a, b) =>
            (Number(a.sortOrder) || 999) - (Number(b.sortOrder) || 999) ||
            (rank[String(a.type)] || 999) - (rank[String(b.type)] || 999) ||
            Number(a.price || 0) - Number(b.price || 0)
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openPlanForm = (plan?: MembershipPlan) => {
    setEditingPlanId(plan?.membershipPlanId || null);
    setDoctorQuery('');
    setDoctorResults([]);
    setPlanForm(
      plan
        ? {
            ...plan,
            allowedDoctors: plan.allowedDoctors || [],
            promoImageOrientation: plan.promoImageOrientation === 'vertical' ? 'vertical' : 'horizontal',
            gstPercent: Number(plan.gstPercent || 0),
            gstMode: plan.gstMode === 'inclusive' ? 'inclusive' : 'exclusive',
            sortOrder: Math.max(1, Number(plan.sortOrder) || 1),
          }
        : {
            ...emptyPlanForm,
            promoImageMediaKey: membershipPromoMediaKey(),
            sortOrder:
              plans.reduce((max, row) => Math.max(max, Number(row.sortOrder) || 0), 0) + 1,
          }
    );
  };

  useEffect(() => {
    if (!planForm) return;
    const q = doctorQuery.trim();
    const timer = window.setTimeout(() => {
      void fetchMembershipPlanDoctors(q)
        .then(setDoctorResults)
        .catch(() => setDoctorResults([]));
    }, q ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [planForm, doctorQuery]);

  const selectedDoctors = planForm?.allowedDoctors || [];

  const doctorKey = (row: { vendorId: string; specialityId?: string; specialityName?: string }) =>
    `${row.vendorId}::${row.specialityId || ''}::${row.specialityName || ''}`;

  const toggleDoctor = (doctor: CatalogDoctor, spec: { specialityId: string; name: string }) => {
    if (!planForm) return;
    const nextRow: MembershipPlanDoctor = {
      vendorId: doctor.vendorId,
      doctorName: doctor.doctorName,
      clinicName: doctor.clinicName || '',
      specialityId: spec.specialityId,
      specialityName: spec.name,
    };
    const exists = selectedDoctors.some((item) => doctorKey(item) === doctorKey(nextRow));
    const next = exists
      ? selectedDoctors.filter((item) => doctorKey(item) !== doctorKey(nextRow))
      : [...selectedDoctors, nextRow];
    setPlanForm({ ...planForm, allowedDoctors: next });
  };

  const uploadPromo = async (file: File) => {
    if (!planForm) return;
    try {
      setUploadingPromo(true);
      const key = planForm.promoImageMediaKey || membershipPromoMediaKey(editingPlanId);
      await uploadMediaAsset({ file, key, name: file.name });
      const pub = await fetchPublicMediaByKey(key);
      setPlanForm({
        ...planForm,
        promoImageMediaKey: key,
        promoImageUrl: pub.fileUrl || null,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to upload promo image');
    } finally {
      setUploadingPromo(false);
    }
  };

  const removePromo = async () => {
    if (!planForm) return;
    const key = planForm.promoImageMediaKey;
    if (key) {
      try {
        const listed = await fetchMediaAssets({ key, limit: 1 });
        const asset = listed.data?.[0];
        if (asset?.id) await deleteMediaAsset(asset.id);
      } catch {
        // Plan can still drop the image reference if media delete fails.
      }
    }
    setPlanForm({
      ...planForm,
      promoImageUrl: null,
      promoImageMediaKey: key || null,
    });
  };

  const savePlan = async () => {
    if (!planForm) return;
    try {
      setBusy(true);
      const payload: Partial<MembershipPlan> = {
        ...planForm,
        allowedSpecialities: [],
        allowedDoctors: planForm.allowedDoctors || [],
        promoImageOrientation:
          planForm.promoImageOrientation === 'vertical' ? 'vertical' : 'horizontal',
        sortOrder: Math.max(1, Math.round(Number(planForm.sortOrder) || 1)),
      };
      if (editingPlanId) {
        await updateMembershipPlan(editingPlanId, payload);
        setSuccess('Plan updated.');
      } else {
        await createMembershipPlan(payload);
        setSuccess('Plan created.');
      }
      setPlanForm(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save plan');
    } finally {
      setBusy(false);
    }
  };

  const setField = (key: keyof MembershipPlan, value: unknown) => {
    if (!planForm) return;
    if (key === 'type') {
      const typed = String(value || '');
      const defaults = PLAN_TYPE_DEFAULTS[typed] || {};
      const { sortOrder: typeOrder, ...rest } = defaults;
      setPlanForm({
        ...planForm,
        type: typed,
        ...rest,
        name: planForm.name || typed,
        sortOrder: editingPlanId ? planForm.sortOrder : typeOrder ?? planForm.sortOrder,
      });
      return;
    }
    setPlanForm({ ...planForm, [key]: value });
  };

  const savePlanOrder = async (plan: MembershipPlan, raw: string) => {
    const next = Math.max(1, Math.round(Number(raw) || 1));
    if (Number(plan.sortOrder) === next) return;
    try {
      setBusy(true);
      setError(null);
      await updateMembershipPlan(plan.membershipPlanId, { sortOrder: next });
      setSuccess(`"${plan.name}" is now order ${next}.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save plan order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <MembershipPanelShell
      title="Plans"
      actions={
        <button type="button" className={msPrimaryBtn} onClick={() => openPlanForm()}>
          <Plus className="mr-2 h-4 w-4" />
          Create plan
        </button>
      }
    >
      <MembershipError message={error} />
      <MembershipSuccess message={success} />
      {loading ? <MembershipLoading /> : null}

      {!loading ? (
        <div className={msTableWrap}>
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className={msTh}>Order</th>
                <th className={msTh}>Plan</th>
                <th className={msTh}>Price</th>
                <th className={msTh}>Consults</th>
                <th className={msTh}>Assigned doctors</th>
                <th className={msTh}>Status</th>
                <th className={msTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.membershipPlanId} className="border-t border-gray-100 dark:border-gray-800">
                  <td className={msTd}>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      disabled={busy}
                      defaultValue={Math.max(1, Number(plan.sortOrder) || 1)}
                      key={`${plan.membershipPlanId}-${plan.sortOrder}`}
                      className={`${msInput} w-16 text-center`}
                      onBlur={(e) => void savePlanOrder(plan, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      aria-label={`${plan.name} display order`}
                    />
                  </td>
                  <td className={msTd}>
                    <div className="flex items-center gap-3">
                      {plan.promoImageUrl ? (
                        <img
                          src={plan.promoImageUrl}
                          alt=""
                          className={`rounded-lg object-cover ${
                            plan.promoImageOrientation === 'vertical' ? 'h-14 w-10' : 'h-10 w-16'
                          }`}
                        />
                      ) : (
                        <div className="flex h-10 w-16 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
                          <ImagePlus className="h-4 w-4" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{plan.name}</div>
                        <div className="text-xs text-gray-500">
                          {plan.type} · {plan.duration}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={msTd}>
                    <div>₹{Number(plan.pricing?.payable ?? plan.price).toLocaleString()}</div>
                    {Number(plan.gstPercent || 0) > 0 ? (
                      <div className="text-xs text-gray-500">
                        GST {Number(plan.gstPercent)}% {plan.gstMode === 'inclusive' ? 'incl.' : 'excl.'}
                      </div>
                    ) : null}
                  </td>
                  <td className={msTd}>{plan.consultationCreditsTotal || plan.consultationCreditsPerMonth}</td>
                    <td className={msTd}>
                      {(plan.allowedDoctors || []).length
                        ? `${plan.allowedDoctors!.length} doctor${plan.allowedDoctors!.length === 1 ? '' : 's'}`
                        : 'None — GP fallback'}
                    </td>
                  <td className={msTd}>
                    <StatusBadge status={plan.isActive ? 'active' : 'inactive'} />
                  </td>
                  <td className={msTd}>
                    <MembershipPlanActionsMenu
                      plan={plan}
                      onView={setViewingPlan}
                      onEdit={openPlanForm}
                      onToggleActive={(row) =>
                        setConfirm({
                          title: row.isActive ? 'Deactivate plan' : 'Activate plan',
                          message: `${row.isActive ? 'Deactivate' : 'Activate'} "${row.name}"?`,
                          onConfirm: async () => {
                            await setMembershipPlanActive(row.membershipPlanId, !row.isActive);
                            await load();
                          },
                        })
                      }
                      onDelete={(row) =>
                        setConfirm({
                          title: 'Delete plan',
                          message: `Delete "${row.name}"?\n\nThe plan will be removed from all listings and can no longer be purchased. All historical memberships and payment records are kept safely. Active members (if any) must be cancelled first.`,
                          onConfirm: async () => {
                            await deleteMembershipPlan(row.membershipPlanId, 'Admin deleted from Plans page');
                            await load();
                          },
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
              {!plans.length ? (
                <tr>
                  <td className={`${msTd} text-gray-500`} colSpan={7}>
                    No plans yet. Create one or seed defaults from Tools.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {viewingPlan ? (
        <MembershipPlanViewModal
          plan={viewingPlan}
          onClose={() => setViewingPlan(null)}
          onEdit={(plan) => {
            setViewingPlan(null);
            openPlanForm(plan);
          }}
        />
      ) : null}

      {planForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-800">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {editingPlanId ? 'Edit plan' : 'Create plan'}
                </h2>
                <p className="text-sm text-gray-500">
                  Assign doctors covered by this plan. For each doctor, pick the speciality that can book without payment.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => setPlanForm(null)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-6 lg:grid-cols-3">
              <section className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Basics</h3>
                {(
                  [
                    ['name', 'Name'],
                    ['title', 'Title'],
                    ['type', 'Type (Starter/Silver/Gold/Platinum)'],
                    ['price', 'Price'],
                    ['duration', 'Duration (1m / 3m / 6m / 12m)'],
                  ] as Array<[keyof MembershipPlan, string]>
                ).map(([key, label]) => (
                  <label key={String(key)} className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
                    {label}
                    <input
                      value={(planForm as any)[key] ?? ''}
                      onChange={(e) =>
                        setField(
                          key,
                          key === 'price' ? Number(e.target.value) : e.target.value
                        )
                      }
                      className={msInput}
                    />
                  </label>
                ))}
                <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
                  Display order
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={planForm.sortOrder ?? 1}
                    onChange={(e) => setField('sortOrder', Math.max(1, Number(e.target.value) || 1))}
                    className={msInput}
                  />
                  <span className="text-xs text-gray-500">
                    1, 2, 3, 4… Plans are shown in this order on admin, app, and web.
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
                  GST %
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={planForm.gstPercent ?? 0}
                    onChange={(e) => setField('gstPercent', Number(e.target.value))}
                    className={msInput}
                  />
                </label>
                <div className="text-sm text-gray-700 dark:text-gray-200">
                  GST type
                  <div className="mt-2 flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="gstMode"
                        checked={(planForm.gstMode || 'exclusive') === 'exclusive'}
                        onChange={() => setField('gstMode', 'exclusive')}
                      />
                      Exclusive (add GST on top)
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="gstMode"
                        checked={planForm.gstMode === 'inclusive'}
                        onChange={() => setField('gstMode', 'inclusive')}
                      />
                      Inclusive (GST already in price)
                    </label>
                  </div>
                </div>
                {(() => {
                  const price = Number(planForm.price || 0);
                  const gstPercent = Math.max(0, Number(planForm.gstPercent || 0));
                  const inclusive = planForm.gstMode === 'inclusive';
                  const gstAmount = gstPercent <= 0
                    ? 0
                    : inclusive
                      ? Math.round((price * gstPercent) / (100 + gstPercent) * 100) / 100
                      : Math.round((price * gstPercent) / 100 * 100) / 100;
                  const payable = gstPercent <= 0
                    ? price
                    : inclusive
                      ? price
                      : Math.round((price + gstAmount) * 100) / 100;
                  return (
                    <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {gstPercent > 0
                        ? `GST ₹${gstAmount.toLocaleString()} (${inclusive ? 'included' : 'extra'}) · Customer pays ₹${payable.toLocaleString()}`
                        : 'GST is 0, so it will stay hidden on the app and web.'}
                    </div>
                  );
                })()}
                <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
                  Description
                  <textarea
                    value={planForm.description || ''}
                    onChange={(e) => setField('description', e.target.value)}
                    className={msInput}
                    rows={4}
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(planForm.isActive)}
                    onChange={(e) => setField('isActive', e.target.checked)}
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(planForm.isPopular)}
                    onChange={(e) => setField('isPopular', e.target.checked)}
                  />
                  Popular
                </label>
              </section>

              <section className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Entitlements</h3>
                <p className="text-xs text-gray-500">
                  Vedika commission is not set here. Settlement uses each doctor&apos;s Vendor Commission rate.
                </p>
                {(
                  [
                    ['maxFamilyMembers', 'Family seats'],
                    ['consultationCreditsPerMonth', 'Credits per month'],
                    ['consultationCreditsTotal', 'Total consults in plan'],
                    ['maxCarryForwardCredits', 'Carry-forward max'],
                    ['storageQuotaBytes', 'Storage bytes'],
                    ['maxTransfersPerYear', 'Transfers during plan'],
                    ['gpConsultFee', 'GP consult fee'],
                    ['dedicatedAmbulanceNumber', 'Dedicated ambulance number'],
                  ] as Array<[keyof MembershipPlan, string]>
                ).map(([key, label]) => (
                  <label key={String(key)} className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-200">
                    {label}
                    <input
                      value={(planForm as any)[key] ?? ''}
                      onChange={(e) =>
                        setField(
                          key,
                          key === 'dedicatedAmbulanceNumber' ? e.target.value : Number(e.target.value)
                        )
                      }
                      className={msInput}
                    />
                  </label>
                ))}
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(planForm.consultationCreditCarryForward)}
                    onChange={(e) => setField('consultationCreditCarryForward', e.target.checked)}
                  />
                  Carry-forward unused consults
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(planForm.ambulancePriority)}
                    onChange={(e) => setField('ambulancePriority', e.target.checked)}
                  />
                  Dedicated ambulance
                </label>
              </section>

              <section className="space-y-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Assigned doctors</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Search a doctor, then tick the speciality covered by this plan. Members skip payment only for those doctor + speciality pairs. Leave empty to allow GP / Family Physician fallback.
                  </p>
                  {selectedDoctors.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {selectedDoctors.map((row) => (
                        <button
                          key={doctorKey(row)}
                          type="button"
                          className="rounded-full bg-sky-50 px-2.5 py-1 text-xs text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                          onClick={() =>
                            toggleDoctor(
                              {
                                vendorId: row.vendorId,
                                doctorName: row.doctorName,
                                clinicName: row.clinicName,
                                specialities: [{ specialityId: row.specialityId, name: row.specialityName }],
                              },
                              { specialityId: row.specialityId, name: row.specialityName }
                            )
                          }
                        >
                          {row.doctorName} · {row.specialityName} ×
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="relative mt-3">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      value={doctorQuery}
                      onChange={(e) => setDoctorQuery(e.target.value)}
                      placeholder="Search doctors or clinics"
                      className={`${msInput} w-full pl-9`}
                    />
                  </div>
                  <div className="mt-3 max-h-72 space-y-2 overflow-y-auto rounded-lg border border-gray-100 p-2 dark:border-gray-800">
                    {doctorResults.map((doctor) => {
                      const assignedForDoctor = selectedDoctors.filter(
                        (item) => item.vendorId === doctor.vendorId
                      );
                      return (
                      <div
                        key={doctor.vendorId}
                        className={`rounded-lg border p-3 ${
                          assignedForDoctor.length
                            ? 'border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-900/20'
                            : 'border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/70'
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{doctor.doctorName}</div>
                        <div className="text-xs text-gray-500">{doctor.clinicName || doctor.vendorId}</div>
                        <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Select speciality
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {doctor.specialities.length ? (
                            doctor.specialities.map((spec) => {
                            const checked = selectedDoctors.some(
                              (item) => doctorKey(item) === doctorKey({ vendorId: doctor.vendorId, specialityId: spec.specialityId, specialityName: spec.name })
                            );
                            return (
                              <label
                                key={`${doctor.vendorId}-${spec.specialityId}-${spec.name}`}
                                className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                                  checked
                                    ? 'border-sky-500 bg-sky-100 text-sky-900 dark:border-sky-400 dark:bg-sky-900/50 dark:text-sky-100'
                                    : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-200'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleDoctor(doctor, spec)}
                                />
                                {spec.name || 'Speciality'}
                              </label>
                            );
                          })
                          ) : (
                            <span className="text-xs text-gray-400">No specialities on this profile</span>
                          )}
                        </div>
                      </div>
                    );
                    })}
                    {!doctorResults.length ? (
                      <p className="px-2 py-3 text-xs text-gray-500">Search to find doctors, then select their speciality.</p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Promo image</h3>
                  <p className="mt-1 text-xs text-gray-500">Shown on plan cards in app and web.</p>
                  <div className="mt-3 flex gap-2">
                    {(['horizontal', 'vertical'] as const).map((orientation) => (
                      <button
                        key={orientation}
                        type="button"
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium capitalize ${
                          (planForm.promoImageOrientation || 'horizontal') === orientation
                            ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                            : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                        }`}
                        onClick={() => setField('promoImageOrientation', orientation)}
                      >
                        {orientation}
                      </button>
                    ))}
                  </div>
                  <div
                    className={`mt-3 overflow-hidden rounded-xl border border-dashed border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40 ${
                      planForm.promoImageOrientation === 'vertical' ? 'aspect-[3/4] max-h-64' : 'aspect-video'
                    }`}
                  >
                    {planForm.promoImageUrl ? (
                      <img src={planForm.promoImageUrl} alt="Plan promo" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full min-h-[120px] items-center justify-center text-xs text-gray-400">
                        {uploadingPromo ? 'Uploading…' : 'No image uploaded'}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <label className={`${msSecondaryBtn} cursor-pointer`}>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingPromo}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadPromo(file);
                          e.target.value = '';
                        }}
                      />
                      {planForm.promoImageUrl ? 'Replace image' : 'Upload image'}
                    </label>
                    {planForm.promoImageUrl ? (
                      <button type="button" className={msSecondaryBtn} onClick={() => void removePromo()}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              </section>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              <button type="button" className={msSecondaryBtn} onClick={() => setPlanForm(null)}>
                Close
              </button>
              <button type="button" disabled={busy} className={msPrimaryBtn} onClick={() => void savePlan()}>
                Save plan
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm) return;
          try {
            setBusy(true);
            await confirm.onConfirm();
            setSuccess('Updated.');
            setConfirm(null);
          } catch (e: any) {
            setError(e?.response?.data?.message || 'Action failed');
          } finally {
            setBusy(false);
          }
        }}
      />
    </MembershipPanelShell>
  );
};

export default MembershipPlansPanel;
